import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  listPettyCashFunds, createPettyCashFund, replenishPettyCashFund,
  listPettyCashTransactions, createPettyCashTransaction, getPettyCashTransaction,
  getPettyCashSummary, approvePettyCashTransaction, rejectPettyCashTransaction,
  voidPettyCashTransaction, exportPettyCashReport, listGLAccounts,
} from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

const CATEGORY_OPTIONS = [
  { value: 'OFFICE_SUPPLIES', label: 'Office Supplies' },
  { value: 'REFRESHMENTS', label: 'Refreshments' },
  { value: 'STATIONERY', label: 'Stationery' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'POSTAGE', label: 'Postage & Courier' },
  { value: 'PRINTING', label: 'Printing & Copying' },
  { value: 'CLEANING', label: 'Cleaning Supplies' },
  { value: 'MEALS', label: 'Meals' },
  { value: 'OTHER', label: 'Other' },
];

function getDefaultDateRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return { date_from: `${y}-${m}-01`, date_to: now.toISOString().slice(0, 10) };
}

const EMPTY_FUND_FORM = { name: '', float_amount: '', gl_account: '' };
const EMPTY_TXN_FORM = { date: new Date().toISOString().slice(0, 10), description: '', amount: '', category: 'OTHER', receipt: null };
const EMPTY_REPLENISH_FORM = { amount: '', date: '', description: '' };

function PettyCashManagement({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [funds, setFunds] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedFund, setSelectedFund] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFundForm, setShowFundForm] = useState(false);
  const [showTxnForm, setShowTxnForm] = useState(false);
  const [showReplenish, setShowReplenish] = useState(false);
  const [replenishData, setReplenishData] = useState(EMPTY_REPLENISH_FORM);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [glAccounts, setGlAccounts] = useState([]);
  const [fundForm, setFundForm] = useState(EMPTY_FUND_FORM);
  const [txnForm, setTxnForm] = useState(EMPTY_TXN_FORM);
  const [viewingTxn, setViewingTxn] = useState(null);

  const [isSubmittingFund, setIsSubmittingFund] = useState(false);
  const [isSubmittingTxn, setIsSubmittingTxn] = useState(false);
  const [isSubmittingReplenish, setIsSubmittingReplenish] = useState(false);
  const isSubmittingFundRef = useRef(false);
  const isSubmittingTxnRef = useRef(false);
  const isSubmittingReplenishRef = useRef(false);

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);
  // Senior approvers (approve/void petty-cash transactions) — mirrors the
  // backend permission fix that now admits ACCOUNTANT.
  const isSenior = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchFunds = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listPettyCashFunds(authAxios);
      setFunds(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load petty cash funds.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage]);

  const fetchTransactions = useCallback(async (fundId) => {
    try {
      const params = { fund: fundId };
      if (dateRange.date_from) params.date__gte = dateRange.date_from;
      if (dateRange.date_to) params.date__lte = dateRange.date_to;
      const data = await listPettyCashTransactions(authAxios, params);
      setTransactions(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load transactions.'); }
  }, [authAxios, showFormMessage, dateRange]);

  const fetchSummary = useCallback(async (fundId) => {
    try {
      const params = { fund: fundId };
      if (dateRange.date_from) params.date_from = dateRange.date_from;
      if (dateRange.date_to) params.date_to = dateRange.date_to;
      const data = await getPettyCashSummary(authAxios, params);
      setSummary(data);
    } catch { showFormMessage('error', 'Failed to load fund summary.'); }
  }, [authAxios, showFormMessage, dateRange]);

  const fetchGLAccounts = useCallback(async () => {
    try {
      const data = await listGLAccounts(authAxios, { is_active: true });
      setGlAccounts(Array.isArray(data) ? data : []);
    } catch { /* silent — non-blocking reference data for the New Fund form */ }
  }, [authAxios]);

  useEffect(() => { fetchFunds(); fetchGLAccounts(); }, [fetchFunds, fetchGLAccounts]);
  useEffect(() => {
    if (selectedFund) { fetchTransactions(selectedFund); fetchSummary(selectedFund); }
  }, [selectedFund, fetchTransactions, fetchSummary]);

  // Auto-open a transaction's detail panel when deep-linked from Accounting Review.
  useEffect(() => {
    const handler = async (e) => {
      const detail = e.detail;
      if (!detail || detail.tab !== 'pettyCash' || detail.type !== 'petty_cash_transaction') return;
      try {
        const txn = await getPettyCashTransaction(authAxios, detail.id);
        setSelectedFund(txn.fund);
        setViewingTxn(txn);
      } catch { showFormMessage('error', 'Failed to load the requested transaction.'); }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, [authAxios, showFormMessage]);

  const handleCreateFund = async (e) => {
    e.preventDefault();
    if (isSubmittingFundRef.current) return;
    isSubmittingFundRef.current = true;
    setIsSubmittingFund(true);
    try {
      const payload = { ...fundForm };
      if (!payload.gl_account) delete payload.gl_account;
      await createPettyCashFund(authAxios, payload);
      showFormMessage('success', 'Fund created with initial float.');
      setShowFundForm(false);
      setFundForm(EMPTY_FUND_FORM);
      fetchFunds();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to create fund.');
    } finally {
      isSubmittingFundRef.current = false;
      setIsSubmittingFund(false);
    }
  };

  const handleCreateTxn = async (e) => {
    e.preventDefault();
    if (isSubmittingTxnRef.current) return;
    const ok = await confirm({
      title: 'Record Spend',
      message: `Record a petty cash spend of ${txnForm.amount ? formatCurrency(txnForm.amount) : 'this amount'}?`,
      confirmText: 'Record',
    });
    if (!ok) return;
    isSubmittingTxnRef.current = true;
    setIsSubmittingTxn(true);
    try {
      const formData = new FormData();
      formData.append('fund', selectedFund);
      formData.append('date', txnForm.date);
      formData.append('description', txnForm.description);
      formData.append('amount', txnForm.amount);
      formData.append('category', txnForm.category);
      formData.append('transaction_type', 'EXPENSE');
      if (txnForm.receipt) formData.append('receipt', txnForm.receipt);
      await createPettyCashTransaction(authAxios, formData);
      showFormMessage('success', 'Transaction recorded.');
      setShowTxnForm(false);
      setTxnForm(EMPTY_TXN_FORM);
      fetchTransactions(selectedFund); fetchSummary(selectedFund); fetchFunds();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to record transaction.');
    } finally {
      isSubmittingTxnRef.current = false;
      setIsSubmittingTxn(false);
    }
  };

  const handleReplenish = async () => {
    const parsedAmount = parseFloat(replenishData.amount);
    if (!replenishData.amount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      showFormMessage('error', 'Please enter a valid amount greater than 0.');
      return;
    }
    if (isSubmittingReplenishRef.current) return;
    const ok = await confirm({
      title: 'Replenish Fund',
      message: `This will add ${formatCurrency(replenishData.amount)} to the float and post a GL entry.`,
      confirmText: 'Replenish',
    });
    if (!ok) return;
    isSubmittingReplenishRef.current = true;
    setIsSubmittingReplenish(true);
    try {
      await replenishPettyCashFund(authAxios, selectedFund, {
        amount: replenishData.amount,
        date: replenishData.date || undefined,
        description: replenishData.description || undefined,
      });
      showFormMessage('success', 'Fund replenished successfully.');
      setShowReplenish(false);
      setReplenishData(EMPTY_REPLENISH_FORM);
      fetchTransactions(selectedFund); fetchSummary(selectedFund); fetchFunds();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.amount?.[0] || 'Failed to replenish fund.';
      showFormMessage('error', detail);
    } finally {
      isSubmittingReplenishRef.current = false;
      setIsSubmittingReplenish(false);
    }
  };

  const refreshViewingTxn = async (txnId) => {
    try {
      const txn = await getPettyCashTransaction(authAxios, txnId);
      setViewingTxn(v => (v && v.id === txnId ? txn : v));
    } catch { /* detail panel will just show stale data until reopened */ }
  };

  const handleApprove = async (txnId) => {
    const ok = await confirm({ title: 'Approve Transaction', message: 'Approve this transaction and post it to the GL?', confirmText: 'Approve' });
    if (!ok) return;
    try {
      await approvePettyCashTransaction(authAxios, txnId);
      showFormMessage('success', 'Transaction approved.');
      fetchTransactions(selectedFund); fetchSummary(selectedFund); fetchFunds();
      if (viewingTxn && viewingTxn.id === txnId) refreshViewingTxn(txnId);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to approve.'); }
  };

  const handleReject = async (txnId) => {
    const ok = await confirm({ title: 'Reject Transaction', message: 'Reject this pending transaction?', confirmText: 'Reject', variant: 'danger' });
    if (!ok) return;
    try {
      await rejectPettyCashTransaction(authAxios, txnId);
      showFormMessage('success', 'Transaction rejected.');
      fetchTransactions(selectedFund); fetchSummary(selectedFund);
      if (viewingTxn && viewingTxn.id === txnId) refreshViewingTxn(txnId);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to reject.'); }
  };

  const handleVoid = async (txnId) => {
    const reason = await confirm({
      title: 'Void Transaction',
      message: 'This will reverse the GL entry for this transaction.',
      confirmText: 'Void',
      variant: 'danger',
      promptMode: true,
      promptLabel: 'Reason for voiding',
      required: true,
    });
    if (!reason) return;
    try {
      await voidPettyCashTransaction(authAxios, txnId, reason);
      showFormMessage('success', 'Transaction voided and GL reversed.');
      fetchTransactions(selectedFund); fetchSummary(selectedFund); fetchFunds();
      if (viewingTxn && viewingTxn.id === txnId) refreshViewingTxn(txnId);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to void.'); }
  };

  const handleExportPDF = async () => {
    try {
      const params = {};
      if (dateRange.date_from) params.date_from = dateRange.date_from;
      if (dateRange.date_to) params.date_to = dateRange.date_to;
      const blob = await exportPettyCashReport(authAxios, selectedFund, params);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const fund = funds.find(f => f.id === selectedFund);
      a.download = `PettyCash_Report_${(fund?.name || 'Fund').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      showFormMessage('success', 'Report downloaded.');
    } catch { showFormMessage('error', 'Failed to export report.'); }
  };

  const openDetailPanel = (txn) => setViewingTxn(txn);
  const closeDetailPanel = () => setViewingTxn(null);

  const resetDateRange = () => setDateRange(getDefaultDateRange());
  const currentFund = useMemo(() => funds.find(f => f.id === selectedFund), [funds, selectedFund]);
  const assetAccounts = useMemo(() => glAccounts.filter(a => a.category === 'ASSET'), [glAccounts]);

  const statusBadge = (s) => {
    if (s === 'PENDING') return 'bg-amber-100 text-amber-700';
    if (s === 'APPROVED') return 'bg-green-100 text-green-700';
    if (s === 'REJECTED') return 'bg-red-100 text-red-600';
    if (s === 'VOIDED') return 'bg-gray-300 text-gray-700 line-through';
    return 'bg-gray-100 text-gray-600';
  };

  const typeBadge = (t) => {
    if (t === 'REPLENISHMENT') return 'bg-green-100 text-green-700';
    if (t === 'REVERSE') return 'bg-orange-100 text-orange-700';
    return 'bg-red-100 text-red-600';
  };

  const typeLabel = (t) => {
    if (t === 'REPLENISHMENT') return 'Replenish';
    if (t === 'REVERSE') return 'Reversal';
    return 'Spend';
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Petty Cash</h2>
          <p className="text-gray-500 text-sm mt-1">Manage petty cash funds and transactions</p>
        </div>
        {canManage && (
          <button onClick={() => setShowFundForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">+ New Fund</button>
        )}
      </div>

      {/* Fund Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {funds.map(f => (
          <div key={f.id} onClick={() => setSelectedFund(f.id)}
            className={`bg-white rounded-xl shadow-sm p-4 cursor-pointer transition-all hover:shadow-md ${selectedFund === f.id ? 'ring-2 ring-blue-500' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800">{f.name}</h3>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${f.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {f.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Float Amount:</span>
                <span className="font-medium">{formatCurrency(f.float_amount || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Current Balance:</span>
                <span className="font-bold text-blue-600">{formatCurrency(f.current_balance || 0)}</span>
              </div>
              {f.custodian_username && (
                <div className="text-xs text-gray-400 mt-1">Custodian: {f.custodian_username}</div>
              )}
              {f.gl_account_name && (
                <div className="text-xs text-gray-400">GL: {f.gl_account_name}</div>
              )}
            </div>
          </div>
        ))}
        {funds.length === 0 && !isLoading && (
          <div className="col-span-full text-center py-8 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500">No petty cash funds. Create one to get started.</p>
          </div>
        )}
      </div>

      {/* Transactions for selected fund */}
      {selectedFund && currentFund && (
        <div>
          {/* Date Range Controls */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Date Range:</span>
              <input type="date" value={dateRange.date_from} onChange={e => setDateRange(d => ({ ...d, date_from: e.target.value }))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
              <span className="text-gray-400">to</span>
              <input type="date" value={dateRange.date_to} onChange={e => setDateRange(d => ({ ...d, date_to: e.target.value }))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
              <button onClick={resetDateRange} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Reset</button>
            </div>
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-xs text-gray-500 uppercase">Float (Replenishments)</p>
                <p className="text-lg font-bold text-green-600 mt-1">{formatCurrency(summary.float_amount || 0)}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-xs text-gray-500 uppercase">Total Expenses</p>
                <p className="text-lg font-bold text-red-600 mt-1">{formatCurrency(summary.total_expenses || 0)}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-xs text-gray-500 uppercase">Current Balance</p>
                <p className="text-lg font-bold text-blue-600 mt-1">{formatCurrency(summary.current_balance || 0)}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-xs text-gray-500 uppercase">Transactions</p>
                <p className="text-lg font-bold text-gray-800 mt-1">{summary.transaction_count || 0}</p>
                {summary.pending_count > 0 && (
                  <p className="text-xs text-amber-600 mt-1">{summary.pending_count} pending approval</p>
                )}
              </div>
            </div>
          )}

          {/* Actions Bar */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">{currentFund.name} – Transactions</h3>
            <div className="flex gap-2">
              <button onClick={handleExportPDF} aria-label="Export petty cash report as PDF" className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium">Export PDF</button>
              {canManage && (
                <>
                  <button onClick={() => setShowReplenish(true)} aria-label="Replenish fund" className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs font-medium">Replenish</button>
                  <button onClick={() => setShowTxnForm(true)} aria-label="Record petty cash spend" className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs font-medium">+ Record Spend</button>
                </>
              )}
            </div>
          </div>

          {/* Transactions Table */}
          {transactions.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-xl shadow-sm"><p className="text-gray-500">No transactions for this period.</p></div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map(t => (
                    <tr key={t.id} className={`hover:bg-gray-50 ${t.status === 'PENDING' ? 'bg-amber-50' : ''}`}>
                      <td className="px-4 py-3 text-gray-700">{t.date}</td>
                      <td className="px-4 py-3 text-gray-800">
                        <button onClick={() => openDetailPanel(t)} aria-label={`View details for transaction: ${t.description}`} className="hover:underline text-left">{t.description}</button>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{t.category_display || CATEGORY_OPTIONS.find(c => c.value === t.category)?.label || t.category}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {t.receipt ? (
                          <a href={t.receipt} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View</a>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(t.amount || 0)}</td>
                      <td className="px-4 py-3 text-right font-medium text-blue-600">
                        {t.running_balance !== undefined && t.running_balance !== null ? formatCurrency(t.running_balance) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeBadge(t.transaction_type)}`}>
                          {typeLabel(t.transaction_type)}
                        </span>
                        {t.category === 'FUNDS_RECEIVED' && (
                          <span className="ml-1 inline-flex px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600">Float</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(t.status)}`}>
                          {t.status_display || t.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <button onClick={() => openDetailPanel(t)} aria-label={`View transaction details: ${t.description}`} className="text-gray-600 hover:text-gray-900 text-xs font-medium">View</button>
                        {isSenior && t.status === 'PENDING' && (
                          <>
                            <button onClick={() => handleApprove(t.id)} aria-label={`Approve transaction: ${t.description}`} className="text-green-600 hover:text-green-800 text-xs font-medium">Approve</button>
                            <button onClick={() => handleReject(t.id)} aria-label={`Reject transaction: ${t.description}`} className="text-red-600 hover:text-red-800 text-xs font-medium">Reject</button>
                          </>
                        )}
                        {isSenior && t.status === 'APPROVED' && t.transaction_type === 'EXPENSE' && (
                          <button onClick={() => handleVoid(t.id)} aria-label={`Void transaction: ${t.description}`} className="text-gray-500 hover:text-red-600 text-xs font-medium" title="Void transaction & reverse GL">Void</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* New Fund Modal */}
      {showFundForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">New Petty Cash Fund</h3>
            <form onSubmit={handleCreateFund} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fund Name *</label>
                <input type="text" required value={fundForm.name} onChange={e => setFundForm({ ...fundForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Office Petty Cash" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Float Amount (BWP) *</label>
                <input type="number" step="0.01" required value={fundForm.float_amount} onChange={e => setFundForm({ ...fundForm, float_amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GL Account (Petty Cash Asset)</label>
                <select value={fundForm.gl_account} onChange={e => setFundForm({ ...fundForm, gl_account: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">Auto (1100 - Petty Cash)</option>
                  {assetAccounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowFundForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" disabled={isSubmittingFund} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmittingFund ? 'Creating…' : 'Create Fund'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Transaction Modal */}
      {showTxnForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Record Petty Cash Spend</h3>
            <form onSubmit={handleCreateTxn} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" required value={txnForm.date} onChange={e => setTxnForm({ ...txnForm, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input type="text" required value={txnForm.description} onChange={e => setTxnForm({ ...txnForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Coffee and water for office" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BWP) *</label>
                  <input type="number" step="0.01" required value={txnForm.amount} onChange={e => setTxnForm({ ...txnForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={txnForm.category} onChange={e => setTxnForm({ ...txnForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt (optional)</label>
                <input type="file" accept="image/*,.pdf" onChange={e => setTxnForm({ ...txnForm, receipt: e.target.files[0] || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              {txnForm.amount && currentFund && parseFloat(txnForm.amount) >= (currentFund.approval_threshold || 1000) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                  This amount exceeds the approval threshold of {formatCurrency(currentFund.approval_threshold || 1000)}.
                  It will require Senior Accountant approval before posting to the GL.
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowTxnForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" disabled={isSubmittingTxn} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmittingTxn ? 'Recording…' : 'Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Replenish Modal */}
      {showReplenish && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Replenish Fund</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BWP) *</label>
                <input type="number" step="0.01" min="0.01" value={replenishData.amount} onChange={e => setReplenishData(d => ({ ...d, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date (optional)</label>
                <input type="date" value={replenishData.date} onChange={e => setReplenishData(d => ({ ...d, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input type="text" value={replenishData.description} onChange={e => setReplenishData(d => ({ ...d, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Monthly replenishment" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowReplenish(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button onClick={handleReplenish} disabled={isSubmittingReplenish} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmittingReplenish ? 'Replenishing…' : 'Replenish'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Detail Panel */}
      {viewingTxn && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailPanel}></div>
            <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
              <div className="w-screen max-w-md">
                <div className="h-full flex flex-col bg-white shadow-xl">
                  <div className="px-4 py-6 sm:px-6 bg-white border-b">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">{viewingTxn.description}</h2>
                        <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${statusBadge(viewingTxn.status)}`}>
                            {viewingTxn.status_display || viewingTxn.status}
                          </span>
                          <span>{viewingTxn.fund_name}</span>
                        </div>
                      </div>
                      <div className="ml-3 h-7 flex items-center">
                        <button onClick={closeDetailPanel} aria-label="Close transaction details" className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">×</button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-1 gap-3 mb-4 rounded-lg border border-gray-200 bg-white p-4">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Amount</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(viewingTxn.amount || 0)}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Date</p>
                          <p className="font-semibold text-gray-900">{viewingTxn.date}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Category</p>
                          <p className="font-semibold text-gray-900">{viewingTxn.category_display || viewingTxn.category}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Type</p>
                          <p className="font-semibold text-gray-900">{viewingTxn.transaction_type_display || viewingTxn.transaction_type}</p>
                        </div>
                      </div>
                      {viewingTxn.created_by_username && (
                        <div className="text-sm">
                          <p className="text-gray-600">Recorded By</p>
                          <p className="font-semibold text-gray-900">{viewingTxn.created_by_username}</p>
                        </div>
                      )}
                      {viewingTxn.receipt && (
                        <div className="text-sm">
                          <p className="text-gray-600">Receipt</p>
                          <a href={viewingTxn.receipt} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">View file</a>
                        </div>
                      )}
                      {viewingTxn.gl_entry_number && (
                        <div className="text-sm">
                          <p className="text-gray-600">GL Journal Entry</p>
                          <p className="font-semibold text-gray-900">{viewingTxn.gl_entry_number}</p>
                        </div>
                      )}
                      {viewingTxn.status === 'VOIDED' && (
                        <div className="rounded-md bg-red-50 px-3 py-2 ring-1 ring-inset ring-red-200 text-sm">
                          <p className="text-red-800 font-medium">Voided</p>
                          <p className="text-red-700">{viewingTxn.void_reason}</p>
                          {viewingTxn.voided_by_username && <p className="text-red-600 text-xs mt-1">By {viewingTxn.voided_by_username} on {viewingTxn.voided_at ? new Date(viewingTxn.voided_at).toLocaleDateString() : ''}</p>}
                        </div>
                      )}
                      {isSenior && viewingTxn.status === 'PENDING' && (
                        <div className="flex gap-2 pt-2 border-t border-gray-100">
                          <button onClick={() => handleApprove(viewingTxn.id)} className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs font-medium">Approve</button>
                          <button onClick={() => handleReject(viewingTxn.id)} className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-xs font-medium">Reject</button>
                        </div>
                      )}
                      {isSenior && viewingTxn.status === 'APPROVED' && viewingTxn.transaction_type === 'EXPENSE' && (
                        <div className="flex gap-2 pt-2 border-t border-gray-100">
                          <button onClick={() => handleVoid(viewingTxn.id)} className="px-3 py-1.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-xs font-medium">Void</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default PettyCashManagement;
